import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Target, Zap, Award } from 'lucide-react';

const About = () => {
  const { t } = useTranslation();
  const values = [
    {
      icon: Shield,
      title: t('about.value1Title'),
      description: t('about.value1Desc')
    },
    {
      icon: Target,
      title: t('about.value2Title'),
      description: t('about.value2Desc')
    },
    {
      icon: Zap,
      title: t('about.value3Title'),
      description: t('about.value3Desc')
    },
    {
      icon: Award,
      title: t('about.value4Title'),
      description: t('about.value4Desc')
    }
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Technical Background */}
      <div className="absolute inset-0 bg-binary-pattern opacity-30" />
      <div className="absolute inset-0 bg-tech-dots" />
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-gradient-to-r from-cyan-100/40 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-l from-teal-100/40 to-transparent rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('about.title')} <span className="bg-gradient-to-r from-cyan-700 to-teal-700 bg-clip-text text-transparent">{t('about.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed font-medium">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Mission Statement */}
        <div className="max-w-5xl mx-auto mb-20 p-8 lg:p-12 bg-gradient-to-br from-slate-100 to-slate-200 border-2 border-gray-300 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{t('about.missionTitle')}</h3>
            <p className="text-lg text-slate-800 leading-relaxed mb-6 font-medium">
              {t('about.mission1')}
            </p>
            <p className="text-lg text-slate-800 leading-relaxed mb-8 font-medium">
              {t('about.mission2')}
            </p>
            
            {/* New Section: Because we are technologists */}
            <h4 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4 mt-8">{t('about.missionSubtitle')}</h4>
            <p className="text-lg text-slate-800 leading-relaxed font-medium">
              {t('about.mission3')}
            </p>
          </div>
        </div>

        {/* Industries Served with Images */}
        <div className="max-w-6xl mx-auto mb-20">
          <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">{t('about.industriesTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: t('about.industry1'),
                image: '/images/industries/semiconductors.jpg',
                description: t('about.industry1Desc')
              },
              {
                name: t('about.industry2'),
                image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80',
                description: t('about.industry2Desc')
              },
              {
                name: t('about.industry3'),
                image: '/images/industries/power.jpg',
                description: t('about.industry3Desc')
              },
              {
                name: t('about.industry4'),
                image: '/images/industries/grid.jpg',
                description: t('about.industry4Desc')
              }
            ].map((industry, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-xl border-2 border-gray-200 hover:border-cyan-600 hover:shadow-2xl transition-all duration-300 bg-white"
              >
                <div className="aspect-w-16 aspect-h-12 overflow-hidden">
                  <img 
                    src={industry.image} 
                    alt={industry.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h4 className="text-lg font-bold mb-1">{industry.name}</h4>
                  <p className="text-xs text-gray-200">{industry.description}</p>
                </div>
                <div className="absolute top-2 right-2 w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Core Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div 
                key={index}
                className="group p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-cyan-600 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-cyan-800 transition-colors">
                    {value.title}
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed font-medium">
                    {value.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
